import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import axios from "axios"
import { setAuthToken } from "modules/auth/lib/setAuthToken"
import { getBackofficeCompaniesQuery } from "modules/backoffice/lib/companies/getBackofficeCompaniesQuery"
import { getBackofficeFlagsActivityQuery } from "modules/backoffice/lib/flags/getBackofficeFlagsActivityQuery"
import { getBackofficeFlagsConversationsQuery } from "modules/backoffice/lib/flags/getBackofficeFlagsConversationsQuery"
import { getBackofficeFlagsTimelineQuery } from "modules/backoffice/lib/flags/getBackofficeFlagsTimelineQuery"
import { getBackofficeFlagsTreemapQuery } from "modules/backoffice/lib/flags/getBackofficeFlagsTreemapQuery"
import { BACKOFFICE_ACTIVITY_TIME_FRAME } from "modules/backoffice/lib/getBackofficeActivityOpacity"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeFlagsConversationsResponseDataSchema,
  BackofficeFlagsKindEnum,
  BackofficeFlagsUnnamespacedNamespace,
  BackofficeImpersonateOrganizationResponseDataSchema,
  ChannelEnum,
  DataResponse,
  GetBackofficeFlagsConversationsSchema,
  TimeFrame,
  TimeFrameEnum,
} from "schema"
import { z } from "zod"
import { BackofficeFlagsActivity } from "./BackofficeFlagsActivity"
import { BackofficeFlagsConversationsTable } from "./BackofficeFlagsConversationsTable"
import { BackofficeFlagsFilters } from "./BackofficeFlagsFilters"
import { BackofficeFlagsTimeline } from "./BackofficeFlagsTimeline"
import { BackofficeFlagsTreemap } from "./BackofficeFlagsTreemap"

const ITEMS_PER_PAGE = 50

type FlagsSearch = z.infer<typeof GetBackofficeFlagsConversationsSchema>
type FilterBy = NonNullable<FlagsSearch["filterBy"]>
type ConversationRow = z.infer<typeof BackofficeFlagsConversationsResponseDataSchema>[number]

type Props = {
  search: FlagsSearch
  routeFullPath: string
  onFilterByChange: (filterBy: FlagsSearch["filterBy"]) => void
  onTimeFrameChange: (timeFrame: TimeFrame) => void
}

export const BackofficeFlags = ({
  search,
  routeFullPath,
  onFilterByChange,
  onTimeFrameChange,
}: Props) => {
  const { t } = useTranslation()
  const { buildLocation } = useRouter()
  const queryClient = useQueryClient()
  const [isCompanyMode, setIsCompanyMode] = useState(false)
  const [selectedCompanyFromTreemap, setSelectedCompanyFromTreemap] = useState<
    { id: string; name: string } | undefined
  >()
  const selectedTimeFrame = search.timeFrame ?? TimeFrameEnum.Last3Days

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: routeFullPath,
    total: ITEMS_PER_PAGE,
  })

  const { searchQuery, query, setQuery } = useSearchQuery({
    search,
    fullPath: routeFullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { data: activityData, isPending: isActivityPending } = useQuery(
    getBackofficeFlagsActivityQuery({
      query: {
        query: query ?? "",
        filterBy: search.filterBy,
        timeFrame: BACKOFFICE_ACTIVITY_TIME_FRAME,
      },
    }),
  )

  const { data: treemapData, isPending: isTreemapPending } = useQuery(
    getBackofficeFlagsTreemapQuery({
      query: {
        query: query ?? "",
        filterBy: search.filterBy,
        groupBy: isCompanyMode ? "company" : "key",
        timeFrame: selectedTimeFrame,
      },
    }),
  )

  const timelineFilterBy = useMemo(() => {
    const filterBy = { ...(search.filterBy ?? {}) }
    delete filterBy.key
    return filterBy
  }, [search.filterBy])

  const { data: timelineData, isPending: isTimelinePending } = useQuery(
    getBackofficeFlagsTimelineQuery({
      query: {
        filterBy: timelineFilterBy,
      },
    }),
  )

  const { data: conversations, isPending } = useQuery(
    getBackofficeFlagsConversationsQuery({
      query: {
        query: query ?? "",
        page,
        total: ITEMS_PER_PAGE,
        filterBy: search.filterBy,
        timeFrame: selectedTimeFrame,
      },
      onSuccess: (data) => setCount(data.pageInfo.count),
    }),
  )

  function updateFilterBy(next: FlagsSearch["filterBy"]) {
    if (!next || Object.keys(next).length === 0) return onFilterByChange(undefined)
    onFilterByChange(next)
  }

  function normalizeKeys(value: unknown): string[] {
    if (typeof value === "string") return [value]
    if (!Array.isArray(value)) return []
    return value.filter((entry): entry is string => typeof entry === "string")
  }

  function setFilterField<K extends keyof FilterBy>(field: K, value: FilterBy[K] | undefined) {
    const next: FilterBy = { ...(search.filterBy ?? {}) }
    if (!value) delete next[field]
    else next[field] = value
    setPage(1)
    updateFilterBy(next)
  }

  function keyMatchesNamespace(key: string, namespace: string) {
    const separatorIndex = key.indexOf(":")
    if (namespace === BackofficeFlagsUnnamespacedNamespace) return separatorIndex <= 0
    return separatorIndex > 0 && key.slice(0, separatorIndex) === namespace
  }

  function setKeysFilter(keys: string[]) {
    const next: FilterBy = { ...(search.filterBy ?? {}) }
    if (keys.length === 0) delete next.key
    else next.key = keys
    setPage(1)
    updateFilterBy(next)
  }

  function selectKeyFromTreemap(key: string) {
    const next: FilterBy = { ...(search.filterBy ?? {}), key: [key] }
    delete next.companyId
    setPage(1)
    updateFilterBy(next)
    setIsCompanyMode(true)
  }

  function selectCompanyFromTreemap(company: { id: string; name: string }) {
    const next: FilterBy = { ...(search.filterBy ?? {}), companyId: company.id }
    delete next.key
    setSelectedCompanyFromTreemap(company)
    setPage(1)
    updateFilterBy(next)
    setIsCompanyMode(false)
  }

  function setNamespaceFilter(namespace: string | undefined) {
    const next: FilterBy = { ...(search.filterBy ?? {}) }
    if (!namespace) {
      delete next.namespace
    } else {
      next.namespace = namespace
      const matchingKeys = normalizeKeys(next.key).filter((key) =>
        keyMatchesNamespace(key, namespace),
      )
      if (matchingKeys.length === 0) delete next.key
      else next.key = matchingKeys
    }
    setPage(1)
    updateFilterBy(next)
  }

  function toggleNamespaceFilter(namespace: string) {
    setNamespaceFilter(search.filterBy?.namespace === namespace ? undefined : namespace)
  }

  const selectedKind = BackofficeFlagsKindEnum.find((kind) => kind === search.filterBy?.kind)
  const selectedNamespace =
    typeof search.filterBy?.namespace === "string" ? search.filterBy.namespace : undefined
  const selectedKeys = useMemo(() => normalizeKeys(search.filterBy?.key), [search.filterBy?.key])
  const selectedCompanyId =
    typeof search.filterBy?.companyId === "string" ? search.filterBy.companyId : undefined
  const selectedCampaignId =
    typeof search.filterBy?.campaignId === "string" ? search.filterBy.campaignId : undefined
  const selectedChannel = Object.values(ChannelEnum).find(
    (channel) => channel === search.filterBy?.channel,
  )
  const shouldIncludeLegacy = search.filterBy?.includeLegacy === true
  const keyTreemapData = treemapData?.groupBy === "key" ? treemapData : undefined

  const { data: selectedCompanyData } = useQuery({
    ...getBackofficeCompaniesQuery({
      query: {
        query: selectedCompanyId,
        page: 1,
        total: 1,
        sortBy: { field: "name", order: "asc" },
      },
    }),
    enabled: !!selectedCompanyId,
  })
  const selectedCompanyName =
    selectedCompanyFromTreemap?.id === selectedCompanyId
      ? selectedCompanyFromTreemap?.name
      : selectedCompanyData?.at(0)?.name

  const namespaceOptions = useMemo(() => {
    const namespaces = new Set(activityData?.namespaces ?? [])
    const includesUnnamespaced =
      selectedNamespace === BackofficeFlagsUnnamespacedNamespace ||
      keyTreemapData?.namespaces.some(
        ({ namespace }) => namespace === BackofficeFlagsUnnamespacedNamespace,
      )
    if (includesUnnamespaced) namespaces.add(BackofficeFlagsUnnamespacedNamespace)

    return [...namespaces].map((namespace) => ({
      value: namespace,
      label:
        namespace === BackofficeFlagsUnnamespacedNamespace
          ? t("backoffice.flags.treemap.unnamespaced")
          : namespace,
    }))
  }, [activityData?.namespaces, keyTreemapData?.namespaces, selectedNamespace, t])

  const keyOptions = useMemo(() => {
    const keys = new Set<string>(selectedKeys)
    for (const namespace of keyTreemapData?.namespaces ?? []) {
      for (const { key } of namespace.keys) keys.add(key)
    }

    return [...keys].sort((a, b) => a.localeCompare(b)).map((key) => ({ value: key, label: key }))
  }, [keyTreemapData?.namespaces, selectedKeys])

  async function onRowClick(row: ConversationRow) {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: row.customer.id },
      search: { tab: "conversations", conversationId: Number(row.conversationId) },
    }).href

    try {
      await navigator.clipboard.writeText([window.location.origin, link].join(""))
    } catch (_error) {
      // Ignore clipboard errors, still attempt impersonation and navigation
    }

    try {
      type ImpersonateResponse = DataResponse<
        z.infer<typeof BackofficeImpersonateOrganizationResponseDataSchema>
      >

      const response = await axios.get<ImpersonateResponse>(
        `/backoffice/organizations/impersonate/${row.organizationId}`,
      )

      setAuthToken({ queryClient, token: response.data.data.token })
      window.open([window.location.origin, link].join(""), "_blank")
    } catch (error) {
      console.error("Failed to impersonate organization from conversation flags table", { error })
    }
  }

  return (
    <>
      <BackofficeFlagsFilters
        query={searchQuery ?? ""}
        setQuery={setQuery}
        selectedTimeFrame={selectedTimeFrame}
        onTimeFrameChange={onTimeFrameChange}
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={(companyId) => setFilterField("companyId", companyId)}
        selectedCampaignId={selectedCampaignId}
        onCampaignChange={(campaignId) => setFilterField("campaignId", campaignId)}
        selectedChannel={selectedChannel}
        onChannelChange={(channel) => setFilterField("channel", channel)}
        selectedKind={selectedKind}
        onKindChange={(kind) => setFilterField("kind", kind)}
        selectedNamespace={selectedNamespace}
        namespaceOptions={namespaceOptions}
        onNamespaceChange={setNamespaceFilter}
        selectedKeys={selectedKeys}
        keyOptions={keyOptions}
        onKeysChange={setKeysFilter}
        shouldIncludeLegacy={shouldIncludeLegacy}
        onIncludeLegacyChange={(includeLegacy) => setFilterField("includeLegacy", includeLegacy)}
        onClearFilters={() => {
          onFilterByChange(undefined)
          setQuery("")
        }}
      />
      <div className="flex flex-col gap-4">
        <BackofficeFlagsTreemap
          data={treemapData}
          isCompanyMode={isCompanyMode}
          isPending={isTreemapPending}
          onCompanyClick={selectCompanyFromTreemap}
          onCompanyModeChange={setIsCompanyMode}
          onNamespaceClick={toggleNamespaceFilter}
          onKeyClick={selectKeyFromTreemap}
          selectedCompanyName={selectedCompanyName}
          selectedKeys={selectedKeys}
        />
        <BackofficeFlagsActivity
          data={activityData}
          isPending={isActivityPending}
          timeFrame={selectedTimeFrame}
        />
        <BackofficeFlagsTimeline data={timelineData} isPending={isTimelinePending} />
        <div className="flex flex-col gap-4 pt-4">
          <BackofficeFlagsConversationsTable
            data={conversations?.data}
            isPending={isPending}
            onRowClick={onRowClick}
          />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            count={count}
          />
        </div>
      </div>
    </>
  )
}
